import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Input, output, signal } from '@angular/core';
import { normalizeTableCellConfig, type TableCellConfig } from '../../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { TableCellConfigEditorComponent } from '../../../components/shared/table-cell-config-editor.component';

export interface DesignLinkColumn {
  field: string;
  label: string;
  cellConfig?: TableCellConfig;
}

/** A Design-only list of the columns chosen for navigation. */
@Component({
  selector: 'app-design-column-navigation',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TableCellConfigEditorComponent],
  template: `
    <section class="cn" aria-label="Column navigation">
      <div class="cn-heading"><strong>Column navigation</strong><span>{{ linkedColumns().length }}</span></div>
      <p class="cn-hint">Add links to selected columns.</p>
      <div class="cn-add">
        <select aria-label="Link column" [value]="candidate()" (change)="candidate.set($any($event.target).value)" [disabled]="!availableColumns().length">
          <option value="">{{ availableColumns().length ? 'Select a column' : 'All columns added' }}</option>
          <option *ngFor="let column of availableColumns()" [value]="column.field">{{ column.label }}</option>
        </select>
        <button type="button" class="cn-add-button" aria-label="Add column link" [disabled]="!candidate()" (click)="add()">+ Add</button>
      </div>
      <p class="cn-empty" *ngIf="!linkedColumns().length">No column links added.</p>
      <div class="cn-link" *ngFor="let column of linkedColumns(); trackBy: trackColumn">
        <div class="cn-row">
          <button type="button" class="cn-edit" [attr.aria-label]="'Edit link for ' + column.label" [attr.aria-expanded]="activeField() === column.field" (click)="toggle(column.field)">
            <span class="cn-name" [title]="column.field">{{ column.label }}</span>
            <span class="cn-mode">{{ modeLabel(column.cellConfig) }}</span>
            <span aria-hidden="true">{{ activeField() === column.field ? '−' : '+' }}</span>
          </button>
          <button type="button" class="cn-remove" [attr.aria-label]="'Remove link for ' + column.label" title="Remove this column's link" (click)="remove(column)">×</button>
        </div>
        <div class="cn-settings" *ngIf="activeField() === column.field">
          <app-table-cell-config-editor [navigationOnly]="true" [config]="column.cellConfig" (configChange)="update(column.field, $event)"></app-table-cell-config-editor>
        </div>
      </div>
    </section>
  `,
  styles: [`
    :host { display: block; min-width: 0; margin-bottom: 12px; color: #344054; font-size: 12px; }
    .cn { padding: 10px; border: 1px solid #e4e7ec; border-radius: 8px; background: #f9fafb; container-type: inline-size; }
    .cn-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .cn-heading strong { font-size: 12px; font-weight: 600; }
    .cn-heading > span { padding: 1px 6px; border-radius: 10px; background: #eaecf0; font-size: 11px; }
    .cn-hint, .cn-empty { margin: 6px 0 10px; color: #667085; font-size: 11px; line-height: 1.4; }
    .cn-empty { margin-bottom: 0; }
    .cn-add { display: flex; gap: 6px; }
    select { flex: 1; width: 0; min-width: 0; height: 32px; border: 1px solid #d0d5dd; border-radius: 5px; background: #fff; color: #344054; padding: 0 7px; font: inherit; }
    button { font: inherit; line-height: 1.3; cursor: pointer; }
    button:focus-visible, select:focus-visible { outline: 2px solid #2970ff; outline-offset: 2px; }
    .cn-add-button { flex: none; padding: 5px 10px; border: 1px solid #b2ccff; border-radius: 5px; background: #fff; color: #175cd3; font-weight: 600; white-space: nowrap; }
    button:disabled, select:disabled { opacity: .5; cursor: default; }
    .cn-link { margin-top: 8px; border: 1px solid #e4e7ec; border-radius: 6px; background: #fff; overflow: hidden; }
    .cn-row { display: flex; align-items: center; gap: 4px; padding: 4px; }
    .cn-edit { display: flex; flex: 1; min-width: 0; align-items: center; gap: 6px; padding: 5px 4px; border: 0; background: transparent; color: #344054; text-align: left; }
    .cn-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
    .cn-mode { flex: none; color: #667085; font-size: 10px; }
    .cn-remove { flex: none; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 4px; background: transparent; color: #667085; font-size: 18px; }
    .cn-remove:hover { background: #fef3f2; color: #b42318; }
    .cn-settings { border-top: 1px solid #eaecf0; padding: 10px; }
    @container (max-width: 200px) { .cn-add { flex-wrap: wrap; } select { width: 100%; flex-basis: 100%; } .cn-mode { display: none; } }
  `],
})
export class ColumnNavigationComponent {
  readonly columns = input<DesignLinkColumn[]>([]);
  readonly activeChange = output<string>();
  @Input() set expandedField(value: string | undefined) { if (value !== undefined) this.activeField.set(value); }
  readonly configChange = output<{ field: string; config: TableCellConfig }>();
  readonly candidate = signal('');
  readonly activeField = signal('');
  private readonly pending = signal<ReadonlySet<string>>(new Set());
  private currentContext = '';
  @Input() set contextKey(value: string) {
    if (value === this.currentContext) return;
    this.currentContext = value;
    this.pending.set(new Set());
    this.candidate.set('');
    this.activeField.set('');
  }
  readonly linkedColumns = computed(() => this.columns().filter(column => this.pending().has(column.field) || this.hasLink(column.cellConfig)));
  readonly availableColumns = computed(() => this.columns().filter(column => !this.linkedColumns().some(link => link.field === column.field)));
  add(): void {
    const field = this.candidate();
    if (!this.availableColumns().some(column => column.field === field)) return;
    this.pending.update(current => new Set([...current, field]));
    this.activeField.set(field);
    this.activeChange.emit(field);
    this.candidate.set('');
  }
  toggle(field: string): void {
    this.activeField.update(current => current === field ? '' : field);
    this.activeChange.emit(this.activeField());
  }
  update(field: string, config: TableCellConfig | undefined): void {
    const next = config ?? {};
    this.configChange.emit({ field, config: this.hasLink(next) ? next : { ...next, display: 'text' } });
  }
  remove(column: DesignLinkColumn): void {
    const config = { ...column.cellConfig };
    delete config.navigation;
    delete config.navigateAction;
    delete config.linkTemplate;
    if (config.display === 'link') delete config.display;
    this.pending.update(current => { const next = new Set(current); next.delete(column.field); return next; });
    if (this.activeField() === column.field) { this.activeField.set(''); this.activeChange.emit(''); }
    this.configChange.emit({ field: column.field, config: { ...normalizeTableCellConfig(config), display: 'text' } });
  }
  modeLabel(config: TableCellConfig | undefined): string {
    return config?.navigation ? 'Dashboard' : config?.navigateAction ? 'Route' : this.hasLink(config) ? 'URL' : 'Not configured';
  }
  trackColumn(_index: number, column: DesignLinkColumn): string { return column.field; }
  private hasLink(config: TableCellConfig | undefined): boolean { return !!(config?.display === 'link' || config?.navigation || config?.navigateAction || config?.linkTemplate); }
}
